import React, { Fragment, useMemo, useRef, useState } from 'react'

import {
	Checkmark12Regular,
	ChevronDown16Regular,
	ChevronDown20Regular,
	ChevronUp16Regular,
	LockClosed16Filled,
	Subtract12Regular,
} from '@fluentui/react-icons'
import { Popover, Transition } from '@headlessui/react'
import type { ArticleOptionProps, StateProps } from '@hooks/useQuestion'
import type { ArticleOption } from '@models/questions.model'

import { autoCompleteStyles } from '../Autocomplete/styles'
import { subMatterArticleStyles } from './styles'

const { ContainerComboboxInput, ScrollOptions, NumberSelected, comboboxInput } =
	autoCompleteStyles
const {
	Trigger,
	Panel,
	Group,
	Row,
	Name,
	Counter,
	Toggle,
	Articles,
	Actions,
	Action,
	ArticleRow,
	Box,
	BoxIndicator,
	Empty,
} = subMatterArticleStyles

type SubMatterOption = { id: number | string; name: string }

type Status = 'none' | 'partial' | 'full'

interface Props {
	subMatters: Array<SubMatterOption>
	articles: Array<ArticleOption>
	selectedSubMatters: Array<StateProps>
	selectedArticles: Array<ArticleOptionProps>
	onChange: (
		subMatters: Array<StateProps>,
		articles: Array<ArticleOptionProps>
	) => void
	disabled?: boolean
	placeholder?: string
	selectedText?: string
}

const normalize = (text: string) =>
	text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

const digits = (text: string) => text.replace(/\D/g, '')

const toArticleOption = (article: ArticleOption): ArticleOptionProps => ({
	id: article.id,
	name: article.name,
	value: article.id,
	subMatterId: article.subMatterId,
	questionCount: article.questionCount,
})

const checkedState: Record<Status, boolean | 'indeterminate'> = {
	none: false,
	partial: 'indeterminate',
	full: true,
}

const Check = ({
	state,
	onToggle,
	label,
}: {
	state: Status
	onToggle: (shiftKey: boolean) => void
	label: string
}) => (
	<Box
		aria-label={label}
		checked={checkedState[state]}
		onClick={(event) => {
			event.stopPropagation()
			onToggle(event.shiftKey)
		}}
	>
		<BoxIndicator>
			{state === 'partial' ? <Subtract12Regular /> : <Checkmark12Regular />}
		</BoxIndicator>
	</Box>
)

export const SubMatterArticleFilter = ({
	subMatters,
	articles,
	selectedSubMatters,
	selectedArticles,
	onChange,
	disabled,
	placeholder = 'SubAssunto',
	selectedText = 'SubAssunto',
}: Props) => {
	const [query, setQuery] = useState('')
	const [expanded, setExpanded] = useState<Set<number>>(new Set())

	const articlesBySubMatter = useMemo(() => {
		const grouped = new Map<number, Array<ArticleOption>>()

		articles.forEach((article) => {
			const list = grouped.get(article.subMatterId) ?? []

			list.push(article)
			grouped.set(article.subMatterId, list)
		})

		return grouped
	}, [articles])

	const selectedSubMatterIds = useMemo(
		() => new Set(selectedSubMatters.map((item) => Number(item.id))),
		[selectedSubMatters]
	)
	const selectedArticleIds = useMemo(
		() => new Set(selectedArticles.map((item) => Number(item.id))),
		[selectedArticles]
	)

	const statusOf = (subMatterId: number): Status => {
		if (!selectedSubMatterIds.has(subMatterId)) return 'none'

		return selectedArticles.some((item) => item.subMatterId === subMatterId)
			? 'partial'
			: 'full'
	}

	const visible = useMemo(() => {
		const term = normalize(query.trim())
		const numeric = digits(query)

		return subMatters.flatMap((subMatter) => {
			const id = Number(subMatter.id)
			const own = articlesBySubMatter.get(id) ?? []

			if (!term) return [{ subMatter, articles: own, forceOpen: false }]

			if (normalize(subMatter.name).includes(term)) {
				return [{ subMatter, articles: own, forceOpen: false }]
			}

			const matching = numeric
				? own.filter((article) => digits(article.name).startsWith(numeric))
				: []

			return matching.length
				? [{ subMatter, articles: matching, forceOpen: true }]
				: []
		})
	}, [subMatters, articlesBySubMatter, query])

	const subMatterOption = (subMatter: SubMatterOption): StateProps => ({
		id: subMatter.id,
		name: subMatter.name,
		value: subMatter.id,
	})

	const withoutArticlesOf = (subMatterId: number) =>
		selectedArticles.filter((item) => item.subMatterId !== subMatterId)

	const setFull = (subMatter: SubMatterOption) => {
		const id = Number(subMatter.id)
		const nextSubMatters = selectedSubMatterIds.has(id)
			? selectedSubMatters
			: [...selectedSubMatters, subMatterOption(subMatter)]

		onChange(nextSubMatters, withoutArticlesOf(id))
	}

	const setNone = (subMatter: SubMatterOption) => {
		const id = Number(subMatter.id)

		onChange(
			selectedSubMatters.filter((item) => Number(item.id) !== id),
			withoutArticlesOf(id)
		)
	}

	const toggleSubMatter = (subMatter: SubMatterOption) =>
		statusOf(Number(subMatter.id)) === 'full'
			? setNone(subMatter)
			: setFull(subMatter)

	const commitArticles = (subMatter: SubMatterOption, next: Array<number>) => {
		const id = Number(subMatter.id)
		const own = articlesBySubMatter.get(id) ?? []

		if (next.length === 0) {
			setNone(subMatter)

			return
		}

		if (next.length === own.length) {
			setFull(subMatter)

			return
		}

		const nextSubMatters = selectedSubMatterIds.has(id)
			? selectedSubMatters
			: [...selectedSubMatters, subMatterOption(subMatter)]

		onChange(nextSubMatters, [
			...withoutArticlesOf(id),
			...own.filter((item) => next.includes(item.id)).map(toArticleOption),
		])
	}

	const markedIds = (subMatterId: number) =>
		statusOf(subMatterId) === 'full'
			? (articlesBySubMatter.get(subMatterId) ?? []).map((item) => item.id)
			: selectedArticles
					.filter((item) => item.subMatterId === subMatterId)
					.map((item) => Number(item.id))

	const applyArticles = (
		subMatter: SubMatterOption,
		base: Array<number>,
		ids: Array<number>,
		check: boolean
	) =>
		commitArticles(
			subMatter,
			check
				? [...base, ...ids.filter((articleId) => !base.includes(articleId))]
				: base.filter((articleId) => !ids.includes(articleId))
		)

	const rangeBetween = (
		shown: Array<ArticleOption>,
		fromId: number,
		toId: number
	) => {
		const from = shown.findIndex((item) => item.id === fromId)
		const to = shown.findIndex((item) => item.id === toId)

		if (from < 0 || to < 0) return [toId]

		return shown
			.slice(Math.min(from, to), Math.max(from, to) + 1)
			.map((item) => item.id)
	}

	const anchor = useRef<{ subMatterId: number; articleId: number } | null>(null)

	const toggleArticle = (
		subMatter: SubMatterOption,
		article: ArticleOption,
		shown: Array<ArticleOption>,
		shiftKey = false
	) => {
		const id = Number(subMatter.id)
		const current = markedIds(id)
		const check = !current.includes(article.id)
		const ids =
			shiftKey && anchor.current?.subMatterId === id
				? rangeBetween(shown, anchor.current.articleId, article.id)
				: [article.id]

		anchor.current = { subMatterId: id, articleId: article.id }
		applyArticles(subMatter, current, ids, check)
	}

	const drag = useRef<{
		timer?: number
		active: boolean
		ignoreClickUntil: number
	}>({ active: false, ignoreClickUntil: 0 })

	const ignoreClick = () => Date.now() < drag.current.ignoreClickUntil

	const startDrag = (
		event: React.TouchEvent,
		subMatter: SubMatterOption,
		article: ArticleOption,
		shown: Array<ArticleOption>
	) => {
		if (event.touches.length !== 1) return

		const id = Number(subMatter.id)
		const base = markedIds(id)
		const check = !base.includes(article.id)
		let last = article.id
		const start = event.touches[0]

		const onMove = (moveEvent: TouchEvent) => {
			const touch = moveEvent.touches[0]

			if (!drag.current.active) {
				const moved = Math.hypot(
					touch.clientX - start.clientX,
					touch.clientY - start.clientY
				)

				if (moved > 10) window.clearTimeout(drag.current.timer)

				return
			}

			moveEvent.preventDefault()
			const row = document
				.elementFromPoint(touch.clientX, touch.clientY)
				?.closest<HTMLElement>(`[data-drag-group="${id}"]`)
			const over = Number(row?.dataset.articleId)

			if (!over || over === last) return

			last = over
			applyArticles(
				subMatter,
				base,
				rangeBetween(shown, article.id, over),
				check
			)
		}

		// Na captura da window, antes do Popover, que fecha em touchend não cancelado.
		const stop = (endEvent: TouchEvent) => {
			window.clearTimeout(drag.current.timer)
			document.removeEventListener('touchmove', onMove)
			window.removeEventListener('touchend', stop, true)
			window.removeEventListener('touchcancel', stop, true)
			if (drag.current.active) {
				if (endEvent.cancelable) endEvent.preventDefault()
				drag.current.ignoreClickUntil = Date.now() + 500
			}
			drag.current.active = false
		}

		document.addEventListener('touchmove', onMove, { passive: false })
		window.addEventListener('touchend', stop, true)
		window.addEventListener('touchcancel', stop, true)

		drag.current.timer = window.setTimeout(() => {
			drag.current.active = true
			anchor.current = { subMatterId: id, articleId: article.id }
			navigator.vibrate?.(15)
			applyArticles(subMatter, base, [article.id], check)
		}, 400)
	}

	const toggleExpanded = (id: number) =>
		setExpanded((prev) => {
			const next = new Set(prev)

			if (next.has(id)) {
				next.delete(id)
			} else {
				next.add(id)
			}

			return next
		})

	const count = selectedSubMatters.length

	return (
		<Popover style={{ position: 'relative', width: '100%' }}>
			<Trigger disabled={disabled}>
				{count > 0 ? selectedText : placeholder}
				<div
					style={{
						display: 'flex',
						gap: '8px',
						marginLeft: 'auto',
						alignItems: 'center',
					}}
				>
					{count > 0 && <NumberSelected>{count}</NumberSelected>}
					{disabled && <LockClosed16Filled />}
					<ChevronDown20Regular />
				</div>
			</Trigger>

			<Transition
				as={Fragment}
				leave='transition ease-in duration-100'
				leaveFrom='opacity-100'
				leaveTo='opacity-0'
				afterLeave={() => setQuery('')}
			>
				<Panel>
					<ContainerComboboxInput>
						<input
							placeholder='Pesquisar sub-assunto ou artigo'
							className={comboboxInput()}
							onChange={(event) => setQuery(event.target.value)}
							value={query}
						/>
					</ContainerComboboxInput>

					<ScrollOptions>
						{visible.length === 0 ? (
							<Empty>Nenhum resultado encontrado</Empty>
						) : (
							visible.map(({ subMatter, articles: shown, forceOpen }) => {
								const id = Number(subMatter.id)
								const own = articlesBySubMatter.get(id) ?? []
								const status = statusOf(id)
								const isOpen = forceOpen || expanded.has(id)
								const marked = selectedArticles.filter(
									(item) => item.subMatterId === id
								).length

								return (
									<Group key={id}>
										<Row
											active={status !== 'none'}
											onClick={() => toggleSubMatter(subMatter)}
										>
											<Check
												state={status}
												label={subMatter.name}
												onToggle={() => toggleSubMatter(subMatter)}
											/>
											<Name>
												{subMatter.name}
												{status === 'partial' && (
													<Counter>
														{marked}/{own.length}
													</Counter>
												)}
											</Name>
											{own.length > 0 && (
												<Toggle
													type='button'
													aria-label={
														isOpen ? 'Esconder artigos' : 'Ver artigos'
													}
													aria-expanded={isOpen}
													onClick={(event) => {
														event.stopPropagation()
														toggleExpanded(id)
													}}
												>
													{isOpen ? (
														<ChevronUp16Regular />
													) : (
														<ChevronDown16Regular />
													)}
												</Toggle>
											)}
										</Row>

										{isOpen && own.length > 0 && (
											<Articles>
												<Actions>
													<Action
														type='button'
														onClick={() => setFull(subMatter)}
													>
														Marcar todos
													</Action>
													<Action
														type='button'
														onClick={() => setNone(subMatter)}
													>
														Limpar
													</Action>
												</Actions>
												{shown.map((article) => {
													const checked =
														status === 'full' ||
														selectedArticleIds.has(article.id)

													return (
														<ArticleRow
															key={article.id}
															active={checked}
															data-drag-group={id}
															data-article-id={article.id}
															onTouchStart={(event) =>
																startDrag(event, subMatter, article, shown)
															}
															onClick={(event) => {
																if (ignoreClick()) return
																toggleArticle(
																	subMatter,
																	article,
																	shown,
																	event.shiftKey
																)
															}}
														>
															<Check
																state={checked ? 'full' : 'none'}
																label={`Art. ${article.name}`}
																onToggle={(shiftKey) => {
																	if (ignoreClick()) return
																	toggleArticle(
																		subMatter,
																		article,
																		shown,
																		shiftKey
																	)
																}}
															/>
															{article.name}
														</ArticleRow>
													)
												})}
											</Articles>
										)}
									</Group>
								)
							})
						)}
					</ScrollOptions>
				</Panel>
			</Transition>
		</Popover>
	)
}
